import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateLanguageRequestSchema,
  type CreateLanguageRequest,
  type LanguageDetail,
  type LanguageListItem,
  type UpdateLanguageRequest,
  UpdateLanguageRequestSchema,
} from '@utazzo/shared';
import {
  UzButton,
  UzDrawer,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSwitch,
} from '@utazzo/ui';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { useCreateLanguage, useUpdateLanguage } from '../use-languages';

interface LanguageFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  language?: LanguageDetail | LanguageListItem | null;
}

const CREATE_DEFAULTS: CreateLanguageRequest = {
  code: '',
  name: '',
  nativeName: null,
  isActive: true,
  sortOrder: 0,
};

const Fields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <>
    <UzFormField name="code">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value.toLowerCase())}
          label="ISO-639-1 code"
          placeholder="e.g. en, hi, es"
          hint="2-3 lowercase letters; unique"
          maxLength={3}
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="English name"
          placeholder="e.g. English, Hindi"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="nativeName">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Native script"
          placeholder="e.g. English, हिन्दी"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="sortOrder">
      {({ field, error }) => (
        <UzNumberInput
          name={field.name}
          label="Sort order"
          precision={0}
          value={field.value === null || field.value === undefined ? 0 : (field.value as number)}
          onValueChange={(v) => field.onChange(v ?? 0)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="isActive">
      {({ field }) => (
        <UzSwitch
          label="Active"
          description="Inactive languages are hidden from pickers."
          checked={field.value !== false}
          onCheckedChange={field.onChange}
          disabled={pending}
        />
      )}
    </UzFormField>
  </>
);

export const LanguageFormDrawer = ({
  open,
  onOpenChange,
  mode,
  language,
}: LanguageFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createLanguage = useCreateLanguage();
  const updateLanguage = useUpdateLanguage();
  const pending = createLanguage.isPending || updateLanguage.isPending;

  const createForm = useForm<CreateLanguageRequest>({
    resolver: zodResolver(CreateLanguageRequestSchema),
    defaultValues: CREATE_DEFAULTS,
  });
  const updateForm = useForm<UpdateLanguageRequest>({
    resolver: zodResolver(UpdateLanguageRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) createForm.reset(CREATE_DEFAULTS);
    else if (language)
      updateForm.reset({
        code: language.code,
        name: language.name,
        nativeName: language.nativeName,
        isActive: language.isActive,
        sortOrder: language.sortOrder,
      });
  }, [open, isCreate, language, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createLanguage.mutateAsync({ ...values, nativeName: values.nativeName?.trim() || null });
    onOpenChange(false);
  });
  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!language) return;
    await updateLanguage.mutateAsync({
      id: language.id,
      payload: { ...values, nativeName: values.nativeName?.trim() || null },
    });
    onOpenChange(false);
  });

  const fields = <Fields pending={pending} />;
  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add language' : `Edit ${language?.name ?? 'language'}`}
      description="Used by Activity and Transfer pickers."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          {fields}
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          {fields}
        </UzForm>
      )}
    </UzDrawer>
  );
};
